import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BookOpen, Map, Castle, Flag, Target, Scroll, Sparkles, FileText, GitBranch, History,
  Users, UserCircle2, Heart, Skull, UsersRound, Package, Wand2, Dices, X,
  Gamepad2, Wrench, ShieldCheck, Table2,
} from "lucide-react";

const DocumentTabsContext = createContext(null);

// Map known route suffixes → label / icon. Entity detail tabs get their label set by the page.
const ROUTE_REGISTRY = {
  "chapters":         { label: "Story",         icon: BookOpen },
  "maps":             { label: "World Atlas",   icon: Map },
  "locations":        { label: "Locations",     icon: Castle },
  "factions":         { label: "Factions",      icon: Flag },
  "quests":           { label: "Quests",        icon: Target },
  "lore":             { label: "Lore",          icon: Scroll },
  "deities":          { label: "Deities",       icon: Sparkles },
  "handouts":         { label: "Handouts",      icon: FileText },
  "tables":           { label: "Tables",        icon: Table2 },
  "relationships":    { label: "Family Trees",  icon: GitBranch },
  "world-history":    { label: "World History", icon: History },
  "characters":       { label: "Players",       icon: Users },
  "npcs":             { label: "NPCs",          icon: UserCircle2 },
  "creatures":        { label: "Creatures",     icon: Skull },
  "party":            { label: "The Party",     icon: UsersRound },
  "items":            { label: "Items",         icon: Package },
  "vtt":              { label: "Tabletop",      icon: Gamepad2 },
  "encounter-builder":{ label: "Encounters",    icon: Target },
  "combat":           { label: "Combat",        icon: ShieldCheck },
  "oracle":           { label: "AI Oracle",     icon: Wand2 },
  "dm-screen":        { label: "DM Screen",     icon: Dices },
  "notes":            { label: "Notes",         icon: BookOpen },
};

const tabsKey = (cid) => `np_doctabs_${cid}`;

const fingerprintFor = (pathname, search) => {
  // Tab identity = pathname + selected query params (type for companions).
  const sp = new URLSearchParams(search || "");
  const type = sp.get("type");
  return pathname + (type ? `?type=${type}` : "");
};

const labelForPath = (pathname, search) => {
  // /campaign/:cid/SUFFIX[/maybeId]
  const m = pathname.match(/\/campaign\/[^/]+\/([^/?#]+)(?:\/([^/?#]+))?/);
  if (!m) return { label: "Page", icon: FileText, isEntity: false };
  const suffix = m[1];
  const entityId = m[2];
  const sp = new URLSearchParams(search || "");
  // Special-case Companions (characters?type=companion)
  if (suffix === "characters" && sp.get("type") === "companion") {
    return { label: "Companions", icon: Heart, isEntity: false };
  }
  const reg = ROUTE_REGISTRY[suffix];
  if (reg && !entityId) return { ...reg, isEntity: false };
  if (entityId) {
    // entity detail — use short suffix label, will be overridden by setCurrentTabLabel
    return { label: reg ? reg.label.replace(/s$/, "") : "Detail", icon: reg?.icon || FileText, isEntity: true };
  }
  return { label: "Page", icon: FileText, isEntity: false };
};

export const DocumentTabsProvider = ({ children }) => {
  const { campaignId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [allTabs, setAllTabs] = useState({});

  // Load tabs from localStorage on campaign mount
  useEffect(() => {
    if (!campaignId) return;
    try {
      const stored = JSON.parse(localStorage.getItem(tabsKey(campaignId))) || [];
      setAllTabs(prev => ({ ...prev, [campaignId]: stored }));
    } catch {
      setAllTabs(prev => ({ ...prev, [campaignId]: [] }));
    }
  }, [campaignId]);

  const tabs = (campaignId && allTabs[campaignId]) || [];
  const activeKey = fingerprintFor(location.pathname, location.search);

  // Persist + add tab when route changes (only inside a campaign)
  useEffect(() => {
    if (!campaignId) return;
    if (!location.pathname.startsWith(`/campaign/${campaignId}`)) return;
    // Don't open a tab for the campaign root itself (no section selected)
    if (location.pathname === `/campaign/${campaignId}` || location.pathname === `/campaign/${campaignId}/`) return;

    const fp = fingerprintFor(location.pathname, location.search);
    const meta = labelForPath(location.pathname, location.search);
    setAllTabs(prev => {
      const cur = prev[campaignId] || [];
      // If the tab already exists (including a customLabel-seeded one from
      // setCurrentTabLabel), leave it untouched. We must NOT overwrite a
      // user-set label with the generic path-derived one.
      if (cur.some(t => t.key === fp)) return prev;
      const next = [...cur, { key: fp, path: location.pathname + (location.search || ""), label: meta.label, iconName: iconNameOf(meta.icon), isEntity: meta.isEntity }];
      try { localStorage.setItem(tabsKey(campaignId), JSON.stringify(next)); } catch { /* ignore */ }
      return { ...prev, [campaignId]: next };
    });
  }, [campaignId, location.pathname, location.search]);

  const persist = useCallback((next) => {
    if (!campaignId) return;
    setAllTabs(prev => ({ ...prev, [campaignId]: next }));
    try { localStorage.setItem(tabsKey(campaignId), JSON.stringify(next)); } catch {}
  }, [campaignId]);

  const closeTab = useCallback((key) => {
    if (!campaignId) return;
    const cur = allTabs[campaignId] || [];
    const idx = cur.findIndex(t => t.key === key);
    if (idx === -1) return;
    const next = cur.filter(t => t.key !== key);
    persist(next);
    // If we closed the active tab, navigate to the neighbour
    if (activeKey === key) {
      const neighbour = next[Math.max(0, idx - 1)] || next[0];
      if (neighbour) navigate(neighbour.path);
      else navigate(`/campaign/${campaignId}/chapters`);
    }
  }, [campaignId, allTabs, activeKey, navigate, persist]);

  const reorderTabs = useCallback((from, to) => {
    if (!campaignId) return;
    const cur = [...(allTabs[campaignId] || [])];
    if (from < 0 || from >= cur.length || to < 0 || to >= cur.length) return;
    const [moved] = cur.splice(from, 1);
    cur.splice(to, 0, moved);
    persist(cur);
  }, [campaignId, allTabs, persist]);

  const setCurrentTabLabel = useCallback((label) => {
    if (!campaignId || !label) return;
    const fp = fingerprintFor(location.pathname, location.search);
    setAllTabs(prev => {
      const cur = prev[campaignId] || [];
      const idx = cur.findIndex(t => t.key === fp);
      // If the tab hasn't been registered yet (this useCallback ran before the
      // pathname-effect committed it), seed it ourselves with the custom label
      // so the override survives the first registration. The path-effect's
      // dedupe (`cur.some(t => t.key === fp)`) will then leave our entry alone.
      if (idx === -1) {
        const meta = labelForPath(location.pathname, location.search);
        const seeded = [...cur, {
          key: fp,
          path: location.pathname + (location.search || ""),
          label,
          iconName: iconNameOf(meta.icon),
          isEntity: meta.isEntity,
          customLabel: true,
        }];
        try { localStorage.setItem(tabsKey(campaignId), JSON.stringify(seeded)); } catch { /* ignore */ }
        return { ...prev, [campaignId]: seeded };
      }
      if (cur[idx].label === label && cur[idx].customLabel) return prev;
      const next = cur.map((t, i) => i === idx ? { ...t, label, customLabel: true } : t);
      try { localStorage.setItem(tabsKey(campaignId), JSON.stringify(next)); } catch { /* ignore */ }
      return { ...prev, [campaignId]: next };
    });
  }, [campaignId, location.pathname, location.search]);

  const openTab = useCallback((path, label) => {
    // Just navigate — provider effect will register the tab. Label override applied if provided.
    navigate(path);
    if (label) {
      // Defer until route effect runs
      setTimeout(() => {
        if (!campaignId) return;
        const fp = fingerprintFor(path.split("?")[0], path.includes("?") ? "?" + path.split("?")[1] : "");
        setAllTabs(prev => {
          const cur = prev[campaignId] || [];
          const next = cur.map(t => t.key === fp ? { ...t, label } : t);
          try { localStorage.setItem(tabsKey(campaignId), JSON.stringify(next)); } catch {}
          return { ...prev, [campaignId]: next };
        });
      }, 80);
    }
  }, [navigate, campaignId]);

  const closeAllTabs = useCallback(() => {
    if (!campaignId) return;
    persist([]);
  }, [campaignId, persist]);

  const closeOtherTabs = useCallback((keep) => {
    if (!campaignId) return;
    const cur = allTabs[campaignId] || [];
    persist(cur.filter(t => t.key === keep));
  }, [campaignId, allTabs, persist]);

  // Keyboard: Alt+W close active; Alt+ArrowLeft/Right cycle tabs
  useEffect(() => {
    const onKey = (e) => {
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
      if (inField) return;
      if (e.altKey && (e.key === "w" || e.key === "W")) {
        e.preventDefault();
        closeTab(activeKey);
      } else if (e.altKey && e.key === "ArrowLeft") {
        e.preventDefault();
        const cur = (campaignId && allTabs[campaignId]) || [];
        const idx = cur.findIndex(t => t.key === activeKey);
        const prev = cur[Math.max(0, idx - 1)];
        if (prev && prev.key !== activeKey) navigate(prev.path);
      } else if (e.altKey && e.key === "ArrowRight") {
        e.preventDefault();
        const cur = (campaignId && allTabs[campaignId]) || [];
        const idx = cur.findIndex(t => t.key === activeKey);
        const nxt = cur[Math.min(cur.length - 1, idx + 1)];
        if (nxt && nxt.key !== activeKey) navigate(nxt.path);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeKey, closeTab, allTabs, campaignId, navigate]);

  return (
    <DocumentTabsContext.Provider value={{ tabs, activeKey, openTab, closeTab, closeAllTabs, closeOtherTabs, reorderTabs, setCurrentTabLabel }}>
      {children}
    </DocumentTabsContext.Provider>
  );
};

export const useDocumentTabs = () => {
  const ctx = useContext(DocumentTabsContext);
  if (!ctx) return { tabs: [], activeKey: null, openTab: () => {}, closeTab: () => {}, closeAllTabs: () => {}, closeOtherTabs: () => {}, reorderTabs: () => {}, setCurrentTabLabel: () => {} };
  return ctx;
};

// ===== Icon serialization (so tabs can persist) =====
const ICONS = {
  BookOpen, Map, Castle, Flag, Target, Scroll, Sparkles, FileText, GitBranch, History,
  Users, UserCircle2, Heart, Skull, UsersRound, Package, Wand2, Dices, Gamepad2, Wrench, ShieldCheck, Table2,
};
const iconNameOf = (Icon) => {
  const name = Object.keys(ICONS).find(k => ICONS[k] === Icon);
  return name || "FileText";
};
const IconByName = (name) => ICONS[name] || FileText;

// ===== Tab Bar =====
export const DocumentTabBar = () => {
  const { tabs, activeKey, closeTab, reorderTabs } = useDocumentTabs();
  const navigate = useNavigate();
  const dragIdx = useRef(null);

  if (!tabs.length) return null;

  return (
    <div className="border-b border-border bg-[#0f0c08] flex items-stretch overflow-x-auto" data-testid="document-tab-bar">
      {tabs.map((t, idx) => {
        const Icon = IconByName(t.iconName);
        const active = t.key === activeKey;
        return (
          <div
            key={t.key}
            draggable
            onDragStart={() => { dragIdx.current = idx; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIdx.current !== null && dragIdx.current !== idx) reorderTabs(dragIdx.current, idx); dragIdx.current = null; }}
            onClick={() => navigate(t.path)}
            onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(t.key); } }}
            className={`group flex items-center gap-1.5 pl-2.5 pr-1 py-1.5 text-xs border-r border-border cursor-pointer transition-colors max-w-[200px] ${
              active
                ? "bg-card text-foreground border-b-2 border-b-primary -mb-px"
                : "text-muted-foreground hover:bg-muted/10 hover:text-foreground"
            }`}
            data-testid={`document-tab-${t.key.replace(/[^a-z0-9]/gi, "-")}`}
            title={t.label}
          >
            <Icon className={`w-3 h-3 flex-shrink-0 ${active ? "text-primary" : ""}`} />
            <span className="truncate flex-1 min-w-0">{t.label}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeTab(t.key); }}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded-sm hover:bg-muted/30 hover:text-destructive flex-shrink-0"
              title="Close (Alt+W when active)"
              data-testid={`close-tab-${t.key.replace(/[^a-z0-9]/gi, "-")}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
